import React from 'react';
import FormControl from 'react-bootstrap/FormControl';
import InputGroup from 'react-bootstrap/InputGroup';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { strings } from '../../../resources/strings';
import { recipeResources } from '../../../resources/recipeResources';
import { RecipeType } from '../../../enums/RecipeType';
import { DiagnosticInformation } from '../../../models/DiagnosticInformation';
import { MultifocalInfo } from '../../../models/MultifocalInfo';
import { Session } from '../../../models/Session';
import { Frame } from '../../../models/Frame';
import { SessionHelper } from '../../../helpers/SessionHelper';
import ServicesContext from '../../../contexts/ServicesContext';
import { NewOrderRecipeInput } from '../../../services/Files/NewOrderRecipe/NewOrderRecipeInput';
import { TabMenu } from '../../../common-components/TabMenu/TabMenu';
import { TabDiagnostic } from './TabDiagnostic/TabDiagnostic';
import { TabMultifocal } from './TabMultifocal/TabMultifocal';
import { Container, TabBody, FormChecks, FormHeaderRow } from './styles';

export class FormRecipeValues {
  id?: number;
  name: string;
  pathToFile: string;
  recipeApproved: boolean = false;
  nasoPupillaryDistanceApproved: boolean = false;
  farDiagnostic?: DiagnosticInformation;
  nearDiagnostic?: DiagnosticInformation;
  recipeType?: RecipeType;
  multifocalInfo?: MultifocalInfo;
  multifocalInfoIsConfirmed: boolean;
  leftEyeHeight?: number | null;
  rightEyeHeight?: number | null;
}

interface IFormRecipeProps {
  orderId?: number;
  orderNumber?: string;
  values?: FormRecipeValues;
  change: (values: FormRecipeValues) => void;
  session?: Session | null;
  frames?: Frame[];
  orderIsPhysicalStore: boolean;
}

const TabFormRecipeOptions = {
  Diagnostic: 'diagnostic',
  Multifocal: 'multifocal'
}

const FormRecipe: React.FC<IFormRecipeProps> = ({
  orderId,
  orderNumber,
  values,
  change,
  session,
  frames,
  orderIsPhysicalStore
}) => {

  const inputFile = React.useRef<any>(null);
  const [activeTab, setActiveTab] = React.useState<string>(TabFormRecipeOptions.Diagnostic);

  const {
    newOrderRecipeService,
    toastService
  } = React.useContext(ServicesContext);

  const recipeHasAnyDnp =
    values?.farDiagnostic?.leftNasoPupillaryDistance || values?.farDiagnostic?.rightNasoPupillaryDistance ||
    values?.nearDiagnostic?.leftNasoPupillaryDistance || values?.nearDiagnostic?.rightNasoPupillaryDistance;
  const recipeHasAnyInformation =
    values?.farDiagnostic?.leftSpherical || values?.farDiagnostic?.leftCylindrical || values?.farDiagnostic?.leftAxis ||
    values?.farDiagnostic?.rightSpherical || values?.farDiagnostic?.rightCylindrical || values?.farDiagnostic?.rightAxis ||
    values?.nearDiagnostic?.leftSpherical || values?.nearDiagnostic?.leftCylindrical || values?.nearDiagnostic?.leftAxis ||
    values?.nearDiagnostic?.rightSpherical || values?.nearDiagnostic?.rightCylindrical || values?.nearDiagnostic?.rightAxis;

  const formHeader = React.useMemo(() => {
    const userCanManageMultifocalRecipe = SessionHelper.canManageMultifocalRecipe(session) || false;

    return (
      <>
        <FormHeaderRow>
          <InputGroup className="mb-1">
            <InputGroup.Text style={{ width: '145px' }}>{`${strings.Name}/${strings.Description}`}</InputGroup.Text>
            <FormControl aria-label={`${strings.Name}/${strings.Description}`} defaultValue={values?.name} onChange={(e: any) => changeName(e.target.value)} />
          </InputGroup>
          <InputGroup className="mb-1" style={{ width: '250px', marginLeft: '10px' }}>
            <InputGroup.Text style={{ minWidth: '50px' }}>{strings.Type}</InputGroup.Text>
            <Form.Select
              aria-label={strings.Type}
              defaultValue={values?.recipeType}
              onChange={(e: any) => changeRecipeType(e.target.value)}
              disabled={!userCanManageMultifocalRecipe}
            >
              <option value={RecipeType.SingleVision}>{recipeResources.SingleVisionRecipeTypeDescription}</option>
              <option value={RecipeType.Bifocal}>{recipeResources.BifocalRecipeTypeDescription}</option>
            </Form.Select>
          </InputGroup>
        </FormHeaderRow>

        <FormHeaderRow>
          <InputGroup className="mb-2">
            <InputGroup.Text style={{ width: '145px' }}>{strings.File}</InputGroup.Text>
            {values?.pathToFile ? <a className='form-control' target='_blank' rel='noreferrer' href={values?.pathToFile || ''}>{strings.Recipe}</a> : <div className='form-control'></div>}
            <Button variant="outline-secondary" onClick={openFile}>{strings.Attach}</Button>
            <input type='file' style={{ display: 'none' }} ref={inputFile} onChange={changeFile} />
          </InputGroup>
        </FormHeaderRow>
      </>
    );
  }, [activeTab, values, session]);

  const tabMenu = React.useMemo(() => {
    const multifocal = values?.recipeType ? +values.recipeType === +RecipeType.Bifocal : false;

    if (multifocal) {
      return (
        <TabMenu
          tabs={[
            { key: TabFormRecipeOptions.Diagnostic, text: `${strings.Diagnostic}/${strings.Height}` },
            { key: TabFormRecipeOptions.Multifocal, text: strings.Multifocal }
          ]}
          activeKey={activeTab}
          change={(key) => setActiveTab(key)}
        />
      );
    }

    return (
      <TabMenu
        tabs={[{ key: TabFormRecipeOptions.Diagnostic, text: strings.Diagnostic }]}
        activeKey={TabFormRecipeOptions.Diagnostic}
      />
    );

  }, [activeTab, values, session]);

  const tab = React.useMemo(() => {
    const multifocal = values?.recipeType ? +values.recipeType === +RecipeType.Bifocal : false;

    if (activeTab === TabFormRecipeOptions.Diagnostic || !multifocal) {
      return (
        <TabBody>
          <TabDiagnostic
            multifocal={multifocal}
            values={values}
            change={(diagnosticValues) => change({ ...diagnosticValues })}
          />
        </TabBody>
      );
    }

    if (activeTab === TabFormRecipeOptions.Multifocal) {
      return (
        <TabBody>
          <TabMultifocal
            values={values}
            change={(multifocalValues) => change({ ...multifocalValues })}
            frames={frames}
          />
        </TabBody>
      );
    }

    return <TabBody></TabBody>;
  }, [activeTab, values, session, frames]);

  const formChecks = React.useMemo(() => {
    if (orderIsPhysicalStore) return null;

    return (
      <FormChecks>
        <Form.Check
          type='checkbox'
          label={strings.ConfirmRecipe}
          checked={values?.recipeApproved || false}
          onChange={(e: any) => changeRecipeApproved()}
          disabled={!recipeHasAnyInformation}
        />
        <Form.Check
          type='checkbox'
          label={strings.ConfirmDNP}
          checked={values?.nasoPupillaryDistanceApproved || false}
          onChange={() => changeNasoPupillaryDistanceApproved()}
          disabled={!recipeHasAnyDnp}
        />
      </FormChecks>
    );
  }, [orderIsPhysicalStore, values, recipeHasAnyDnp, recipeHasAnyInformation]);

  return (
    <Container>
      {formHeader}
      {tabMenu}
      {tab}
      {formChecks}
    </Container>
  );

  function changeName(value: string) {
    if (!values) values = new FormRecipeValues();
    values.name = value;
    change({ ...values });
  }

  function changeRecipeType(value?: RecipeType) {
    const bifocal = value ? +value === +RecipeType.Bifocal : false;

    if (!bifocal) {
      setActiveTab(TabFormRecipeOptions.Diagnostic);
    }

    if (bifocal && values && !values.multifocalInfo) {
      values.multifocalInfoIsConfirmed = false;
    }

    if (!values) values = new FormRecipeValues();
    values.recipeType = value;
    change({ ...values });
  }

  function changeRecipeApproved() {
    if (!values) values = new FormRecipeValues();
    values.recipeApproved = !values.recipeApproved;
    change({ ...values });
  }

  function changeNasoPupillaryDistanceApproved() {
    if (!values) values = new FormRecipeValues();
    values.nasoPupillaryDistanceApproved = !values.nasoPupillaryDistanceApproved;
    change({ ...values });
  }

  function openFile() {
    inputFile.current.click();
  }

  function changeFile(event: any) {
    event.stopPropagation();
    event.preventDefault();

    const orderIdValue = orderId || 0;
    const orderNumberValue = orderNumber || orderIdValue;
    const recipeIdValue = values?.id || 0;

    var file = event.target.files[0];

    const name = file.name.replace(/\s/g, '_');
    const fileName = `order_${orderNumberValue}_recipe_${recipeIdValue}_file_${name}`;

    const formData = new FormData();
    formData.append('orderId', orderIdValue.toString());
    formData.append(fileName, file, fileName);

    const input = new NewOrderRecipeInput();
    input.formData = formData;

    newOrderRecipeService.handle(input)
      .then(result => {
        if (result?.value?.value) {
          if (!values) values = new FormRecipeValues();
          values.pathToFile = result?.value?.value;
          toastService.success(strings.FileSuccessfullyAttached);
          change({ ...values });
        }
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongSendTheData);
      });

    inputFile.current.value = '';
  }
}

export { FormRecipe };